import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { useTheme, toBengaliNum } from "../context/ThemeContext";
import { Users, FileText, Beaker, Upload, UserPlus, ChevronRight, Camera, TrendingUp } from "lucide-react";

const TYPE_META = {
  Hematology:  { color: "#2563EB", bg: "#DBEAFE", textColor: "#1E40AF" },
  Hormone:     { color: "#7C3AED", bg: "#F5F3FF", textColor: "#5B21B6" },
  Immunology:  { color: "#0D9488", bg: "#CCFBF1", textColor: "#0F766E" },
  Biochemical: { color: "#16A34A", bg: "#DCFCE7", textColor: "#166534" },
  Other:       { color: "#64748B", bg: "#F1F5F9", textColor: "#475569" },
};

const ROLE_LABEL = { admin: "Administrator", doctor: "Doctor", technician: "Technician", receptionist: "Receptionist" };

function initials(name) {
  if (!name) return "??";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

const AVATAR_COLORS = [
  { bg: "#EEF2FF", color: "#4F46E5" },
  { bg: "#F5F3FF", color: "#7C3AED" },
  { bg: "#DCFCE7", color: "#16A34A" },
  { bg: "#DBEAFE", color: "#2563EB" },
  { bg: "#CCFBF1", color: "#0D9488" },
  { bg: "#FEF3C7", color: "#D97706" },
];
function avatarColor(name) {
  const idx = (name || "").charCodeAt(0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

export default function Dashboard({ setActive, setSelectedPatient }) {
  const { user, pagination } = useApp();
  const { t, language } = useTheme();
  const [dashStats,     setDashStats]     = useState(null);
  const [patientTotal,  setPatientTotal]  = useState(0);
  const [newThisMonth,  setNewThisMonth]  = useState(0);
  const [loading,       setLoading]       = useState(true);

  useEffect(() => {
    import("../utils/api").then(({ reportAPI, patientAPI }) => {
      const thisMonth = new Date().toISOString().slice(0, 7);
      Promise.all([
        reportAPI.list({ page: 1, pageSize: 5 }),
        patientAPI.list("", 1, 1),
        patientAPI.list("", 1, 200),
      ]).then(([repData, patData, allPats]) => {
        setDashStats(repData);
        setPatientTotal(patData.total ?? 0);
        const count = (allPats.patients || []).filter(p =>
          (p.registered || "").startsWith(thisMonth)
        ).length;
        setNewThisMonth(count);
        setLoading(false);
      }).catch(() => setLoading(false));
    });
  }, []);

  const totalPatients = patientTotal || pagination?.total || 0;
  const totalReports  = dashStats?.stats?.total ?? 0;
  const totalTypes    = dashStats?.stats?.test_types || 0;
  const recent        = dashStats?.reports || [];

  const typeCount = recent.reduce((acc, r) => {
    if (r.type) acc[r.type] = (acc[r.type] || 0) + 1;
    return acc;
  }, {});
  const typeTotal = Object.values(typeCount).reduce((a, b) => a + b, 0);

  const kpis = [
    {
      Icon: Users,
      label: t.totalPatients,
      value: totalPatients,
      badge: newThisMonth > 0 ? `+${newThisMonth} this month` : null,
      badgeColor: "#16A34A",
      badgeBg: "#DCFCE7",
      iconBg: "#EEF2FF",
      iconColor: "#4F46E5",
      valColor: "#4F46E5",
    },
    {
      Icon: FileText,
      label: t.totalReports,
      value: totalReports,
      badge: t.allUploaded,
      badgeColor: "#64748B",
      badgeBg: "#F1F5F9",
      iconBg: "#DBEAFE",
      iconColor: "#2563EB",
      valColor: "#2563EB",
    },
    {
      Icon: Beaker,
      label: t.testCategories,
      value: totalTypes,
      badge: t.reportTypes,
      badgeColor: "#64748B",
      badgeBg: "#F1F5F9",
      iconBg: "#CCFBF1",
      iconColor: "#0D9488",
      valColor: "#0D9488",
    },
  ];

  const quickLinks = [
    { label: t.allPatients,   Icon: Users,    action: () => setActive("patients"), color: "#4F46E5", bg: "#EEF2FF" },
    { label: t.labReports,    Icon: FileText, action: () => setActive("reports"),  color: "#2563EB", bg: "#DBEAFE" },
    { label: t.imaging,       Icon: Camera,   action: () => setActive("imaging"),  color: "#0D9488", bg: "#CCFBF1" },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", marginBottom: 3 }}>
            {t.welcome}, {ROLE_LABEL[user?.role] || user?.username}
          </h1>
          <p style={{ color: "#64748B", fontSize: 13 }}>
            Nashman Institute — Pathology &amp; Regenerative Medicine EMR
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setActive("patients")}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <Upload size={13} />
            {t.uploadReport}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setActive("patients")}
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, padding: "7px 14px" }}
          >
            <UserPlus size={14} />
            {t.registerPatient}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 18 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: "16px 18px" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ width: 38, height: 38, borderRadius: 9, background: k.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <k.Icon size={18} color={k.iconColor} />
              </div>
              {k.badge && (
                <span style={{ fontSize: 11, fontWeight: 500, color: k.badgeColor, background: k.badgeBg, padding: "3px 9px", borderRadius: 20 }}>
                  {k.badge}
                </span>
              )}
            </div>
            <div style={{ fontSize: 30, fontWeight: 700, color: k.valColor, lineHeight: 1, marginBottom: 5 }}>
              {loading ? "—" : toBengaliNum(k.value, language)}
            </div>
            <div style={{ fontSize: 12, color: "#64748B" }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Main grid */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 240px", gap: 14 }}>

        {/* Recent reports */}
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: "#0F172A" }}>{t.recentLabReports}</div>
            <button className="btn btn-ghost btn-sm" onClick={() => setActive("reports")} style={{ fontSize: 12 }}>
              {t.viewAll}
            </button>
          </div>
          <div>
            {loading ? (
              <div style={{ padding: "24px 16px", color: "#94A3B8", fontSize: 13, textAlign: "center" }}>Loading...</div>
            ) : recent.length === 0 ? (
              <div className="empty-state"><p>No reports yet.</p></div>
            ) : recent.map((r, i) => {
              const av   = avatarColor(r.patient_name);
              const init = initials(r.patient_name);
              const meta = TYPE_META[r.type] || TYPE_META.Other;
              const isLast = i === recent.length - 1;
              return (
                <div
                  key={r.report_id}
                  onClick={() => { setSelectedPatient(r.patient_id); setActive("patients"); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "11px 16px",
                    borderBottom: isLast ? "none" : "1px solid #F8FAFC",
                    cursor: "pointer",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <div style={{ width: 34, height: 34, borderRadius: "50%", background: av.bg, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                    {init}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#0F172A", marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.patient_name}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ background: meta.bg, color: meta.textColor, padding: "1px 8px", borderRadius: 4, fontSize: 10, fontWeight: 500 }}>{r.type}</span>
                      <span style={{ fontSize: 11, color: "#94A3B8" }}>{r.lab}</span>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: "#94A3B8", flexShrink: 0 }}>{r.date}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

          {/* By report type */}
          <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ padding: "12px 14px", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center", gap: 6 }}>
              <TrendingUp size={13} color="#64748B" />
              <span style={{ fontWeight: 600, fontSize: 13, color: "#0F172A" }}>{t.byReportType}</span>
            </div>
            <div style={{ padding: "12px 14px" }}>
              {Object.keys(typeCount).length === 0 ? (
                <div style={{ color: "#94A3B8", fontSize: 12, textAlign: "center", padding: "8px 0" }}>No data yet</div>
              ) : Object.entries(typeCount).map(([type, count]) => {
                const meta = TYPE_META[type] || TYPE_META.Other;
                const pct  = typeTotal > 0 ? Math.round((count / typeTotal) * 100) : 0;
                return (
                  <div key={type} style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: meta.color, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: "#334155" }}>{type}</span>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: meta.color }}>{count}</span>
                    </div>
                    <div style={{ height: 5, background: "#F1F5F9", borderRadius: 99, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: meta.color, borderRadius: 99 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick access */}
          <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: "#0F172A", marginBottom: 10 }}>{t.quickAccess}</div>
            {quickLinks.map((link, i) => (
              <div
                key={link.label}
                onClick={link.action}
                style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "9px 0",
                  cursor: "pointer",
                  borderBottom: i < quickLinks.length - 1 ? "1px solid #F8FAFC" : "none",
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = "0.6"}
                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
              >
                <div style={{ width: 30, height: 30, borderRadius: 7, background: link.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <link.Icon size={14} color={link.color} />
                </div>
                <span style={{ fontSize: 13, color: "#334155", fontWeight: 500, flex: 1 }}>{link.label}</span>
                <ChevronRight size={13} color="#CBD5E1" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}